import React, { useCallback, useRef } from 'react';
import { Trip, TransportationOption } from '@/types';
import { transportationOptionsService } from '@/services/transportationOptionsService';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import TripDashboardSection from '../TripDashboardSection';
import TransportationOptionsPage from '@/components/transportation/TransportationOptionsPage';
import AddTransportationOptionForm from '@/components/transportation/AddTransportationOptionForm';
import { useI18n } from '@/hooks/useI18n';

interface TripTransportationOptionsPageProps {
  trip: Trip;
}

const TripTransportationOptionsPage: React.FC<TripTransportationOptionsPageProps> = ({ trip }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useI18n();

  const addTransportationOption = useCallback(async (newTransportationOption: Omit<TransportationOption, 'id'>) => {
    await transportationOptionsService.addTransportationOption(trip, newTransportationOption);
  }, [trip]);

  const updateTransportationOption = useCallback(async (updatedTransportationOption: TransportationOption) => {
    await transportationOptionsService.updateTransportationOption(trip, updatedTransportationOption);
  }, [trip]);

  const deleteTransportationOption = useCallback(async (transportationOptionId: string) => {
    await transportationOptionsService.deleteTransportationOption(trip, transportationOptionId);
  }, [trip]);

  return (
    <>
      <TripDashboardSection
        icon="🚗"
        title={t('trip.pages.transportationOptions.title')}
        subtitle={t('trip.pages.transportationOptions.subtitle')}
        description={t('trip.pages.transportationOptions.description')}
        primaryAction={{
          icon: <Add />,
          onClick: () => {
            triggerRef.current?.click();
          },
          label: t('trip.pages.transportationOptions.addTransportationOption')
        }}
      >
        <TransportationOptionsPage 
          transportationOptions={trip.transportationOptions} 
          onUpdateTransportationOption={updateTransportationOption} 
          onAddTransportationOption={addTransportationOption} 
          onDeleteTransportationOption={deleteTransportationOption} 
        />
      </TripDashboardSection>

      <AddTransportationOptionForm
        onAddTransportationOption={addTransportationOption}
        trigger={
          <Button 
            ref={triggerRef}
            style={{ display: 'none' }}
          >
            {t('trip.pages.transportationOptions.hidden')}
          </Button>
        }
      />
    </>
  );
};

export default TripTransportationOptionsPage;